import { Box, Button, Flex, Grid, Icon, Input, Text } from '@chakra-ui/react';
import React, { useRef } from 'react';

import { formatFileSize } from '@/pages/externals/pos-menu-masters/service';
import { MosPosFileMeta } from '@/services/MosPosMenuMaster';

/** 設計書 No.2/No.3 の確定上限。 */
const MAX_ROWS_LABEL = '3,000';
const COLUMN_COUNT_LABEL = '14列';

type Props = {
  masterFile?: File;
  relationFile?: File;
  files?: { master: MosPosFileMeta; relation: MosPosFileMeta };
  masterRows?: number;
  relationRows?: number;
  disabled: boolean;
  onSelect: (kind: 'master' | 'relation', file?: File) => void;
  children?: React.ReactNode;
};

type RowProps = {
  label: string;
  fileName: string;
  selected?: File;
  meta?: MosPosFileMeta;
  rows?: number;
  disabled: boolean;
  testId: string;
  onSelect: (file?: File) => void;
};

const FileRow: React.FC<RowProps> = ({
  label,
  fileName,
  selected,
  meta,
  rows,
  disabled,
  testId,
  onSelect,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const selectedName = meta?.name ?? selected?.name;
  const hasSelectedFile = selectedName !== undefined;

  return (
    <Grid
      templateColumns="1fr"
      sx={{
        '@media (min-width: 861px)': {
          gridTemplateColumns: '220px minmax(220px, 1fr) auto',
        },
      }}
      style={{
        alignItems: 'center',
        background: '#F5F8FB',
        border: '1px solid #E2E8F0',
        borderRadius: '5px',
        gap: '14px',
        padding: '14px 16px',
      }}
      data-testid={`${testId}-row`}
    >
      <Box>
        <Flex align="center" style={{ gap: '4px' }}>
          <Text fontSize="13px" fontWeight="bold">
            {label}
          </Text>
          <Text as="span" color="#EB4458" fontSize="11px">
            必須
          </Text>
        </Flex>
        <Text
          fontSize="11px"
          color="#718096"
          fontFamily="mono"
          mt="3px"
          data-testid={`${testId}-hint`}
        >
          {fileName} （{COLUMN_COUNT_LABEL}・最大{MAX_ROWS_LABEL}行）
        </Text>
      </Box>

      <Box
        flex="1"
        minW="220px"
        style={{
          background: hasSelectedFile ? '#F0FFF4' : '#FFFFFF',
          border: `1px ${hasSelectedFile ? 'solid' : 'dashed'} ${
            hasSelectedFile ? '#9AE6B4' : '#CBD5E0'
          }`,
          borderRadius: '4px',
          padding: '8px 12px',
        }}
      >
        <Flex align="center" style={{ gap: '10px' }}>
          {hasSelectedFile && (
            <Icon
              viewBox="0 0 24 24"
              color="green.700"
              data-testid="selected-file-icon"
            >
              <path
                fill="currentColor"
                d="M6 2h8l4 4v16H6V2zm8 1.5V7h3.5L14 3.5zM8 10v1.5h8V10H8zm0 3v1.5h8V13H8zm0 3v1.5h5V16H8z"
              />
            </Icon>
          )}
          <Text
            fontSize="13px"
            fontWeight={hasSelectedFile ? 600 : 400}
            minW={0}
            title={selectedName}
            data-testid={`${testId}-name`}
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {selectedName ?? '未選択'}
          </Text>
          {meta && (
            <Text
              fontSize="11px"
              color="#718096"
              style={{ marginLeft: 'auto', whiteSpace: 'nowrap' }}
              data-testid={`${testId}-meta`}
            >
              {/* 行数とバイト数は API が返した実測値を表示します */}
              {rows !== undefined ? `${rows} 行 · ` : ''}
              {formatFileSize(meta.size_bytes)}
            </Text>
          )}
        </Flex>
      </Box>

      <Input
        ref={inputRef}
        type="file"
        // 拡張子の指定は UX 上の目安です。実際の判定はバックエンドが行います。
        accept=".csv,text/csv"
        display="none"
        data-testid={`${testId}-input`}
        disabled={disabled}
        onChange={(e) => onSelect(e.target.files?.[0])}
      />
      <Button
        size="sm"
        variant="outline"
        bg="white"
        borderColor="gray.300"
        style={{ borderRadius: '4px', fontSize: '12px', fontWeight: 700 }}
        isDisabled={disabled}
        onClick={() => inputRef.current?.click()}
      >
        ファイルを選択
      </Button>
    </Grid>
  );
};

/** SCR010 のファイル選択。両方揃った時点で自動的に検証が走ります。 */
export const FileUploadSection: React.FC<Props> = ({
  masterFile,
  relationFile,
  files,
  masterRows,
  relationRows,
  disabled,
  onSelect,
  children,
}) => (
  <Box
    style={{
      background: '#FFFFFF',
      border: '1px solid #E2E8F0',
      borderRadius: '5px',
      boxShadow: '0 1px 2px rgba(45,55,72,0.04)',
      overflow: 'hidden',
    }}
    data-testid="upload-card"
  >
    <Flex
      align="center"
      style={{
        borderBottom: '1px solid #E2E8F0',
        gap: '10px',
        padding: '15px 20px',
      }}
      data-testid="upload-section-head"
    >
      <Text fontSize="15px" fontWeight="bold">
        ファイルを選択
      </Text>
      <Text fontSize="12px" color="#718096" style={{ marginLeft: 'auto' }}>
        UTF-8（BOMあり・なし可） / カンマ区切り（CSV）
      </Text>
    </Flex>
    <Box style={{ padding: '20px' }} data-testid="upload-card-body">
      <Text
        fontSize="13px"
        color="#4A5568"
        lineHeight="1.7"
        mb="14px"
        data-testid="upload-description"
      >
        <Text as="span" fontWeight="bold" color="#2D3748">
          親商品マスタ
        </Text>
        （MOS_POS_MENU_MASTER.csv・{COLUMN_COUNT_LABEL}）と、それに紐づく
        <Text as="span" fontWeight="bold" color="#2D3748">
          子商品リレーション
        </Text>
        （MOS_POS_MENU_RELATION.csv・{COLUMN_COUNT_LABEL}
        ／セット構成・チョイス・付属品）の2ファイルを取り込みます。
        <Text as="span" fontWeight="bold" color="#2D3748">
          両方が選択された時点で
        </Text>
        バリデーションを自動実行します（片方だけではバリデーションは走りません）。
      </Text>
      <Text
        fontSize="12px"
        color="#718096"
        mb="14px"
        data-testid="upload-limit-note"
      >
        ※ 各ファイル最大{MAX_ROWS_LABEL}
        行まで対応（確定した上限）。POSメニューコードは半角数字6桁。文字コードはUTF-8（BOMの有無は問いません）、1行目の見出しは所定の列名と完全に一致している必要があります。
      </Text>
      <Box
        style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
        data-testid="upload-rows"
      >
        <FileRow
          label="親商品マスタ"
          fileName="MOS_POS_MENU_MASTER.csv"
          selected={masterFile}
          meta={files?.master}
          rows={masterRows}
          disabled={disabled}
          testId="master-file"
          onSelect={(file) => onSelect('master', file)}
        />
        <FileRow
          label="子商品リレーション"
          fileName="MOS_POS_MENU_RELATION.csv"
          selected={relationFile}
          meta={files?.relation}
          rows={relationRows}
          disabled={disabled}
          testId="relation-file"
          onSelect={(file) => onSelect('relation', file)}
        />
      </Box>
      {children && (
        <Box style={{ marginTop: '18px' }} data-testid="upload-validation-area">
          <Text
            fontSize="12px"
            fontWeight="extrabold"
            color="#4A5568"
            mb="10px"
          >
            アップロードファイル情報 / フォーマットバリデーション /
            参照整合性バリデーション
            <Text as="span" color="#718096" fontSize="11px" fontWeight="normal">
              {' '}
              —
              ファイルを選択すると自動でチェックします（この時点では登録されません）
            </Text>
          </Text>
          {children}
        </Box>
      )}
    </Box>
  </Box>
);
